// DTOs para el Wizard de Instructivo de Embarque (v1 — alcance final 2026-04-27)
// Ver doc/instructivo-embarque/09-final-scope-v1.md para el alcance consolidado.
//
// Decisiones clave aplicadas en este DTO:
// - TypeOfShipmentId a nivel HEADER (no por ítem). 1=Contenedor, 2=Pallet.
// - Antecedentes Financieros a nivel HEADER (Cláusula venta, Forma pago, etc.).
// - Notify es OBLIGATORIO (feedback 2026-04-27).
// - Cut Off incluye HORA (ISO datetime), no solo fecha.
// - 1 booking ↔ 1 I/E activo (UNIQUE constraint en BookingId).
// - Estado fijo "Activo" al crear (sin Borrador, sin Cancelado).
// - idempotencyKey UUID generado en frontend para anti-doble-click.

// ─── ENUMS Y CONSTANTES ─────────────────────────────────────────────────────

/** Tipo de embarque a nivel del Instructivo (HEADER). Replica `tblTypeOfShipments`. */
export enum TypeOfShipment {
  Container = 1,
  Pallet = 2,
}

/** Condición de Flete (feedback 2026-04-27). Si el legacy tiene tabla, mapear a sus IDs. */
export enum FreightCondition {
  Collect = 'COLLECT',
  FullCollect = 'FULL_COLLECT',
  Prepaid = 'PREPAID',
  FullPrepaid = 'FULL_PREPAID',
}

/** Estado del envío de email post-creación. */
export type EmailStatus = 'OK' | 'PENDIENTE_DATOS' | 'FAILED';

// ─── REQUEST DTOs ───────────────────────────────────────────────────────────

export interface WizardInitParamsDto {
  voyageId: number;
  portOfLoadingId: number;
  portOfDischargeId: number;
}

export interface ApplyBookingDto {
  voyageId: number;
  /** Si null/undefined → flujo "sin booking" (externo, todo manual). */
  bookingId?: number;
}

/**
 * Payload completo del Instructivo a crear.
 * Estado siempre "Activo" en v1. Backend infiere statusId desde catálogo.
 */
export interface CreateShippingInstructionDto {
  /** UUID v4 generado por el frontend al montar Paso 4. Anti-doble-click + idempotencia. */
  idempotencyKey: string;

  // ─── Viaje (read-only del itinerario) ────────────────────────────────────
  voyageId: number;
  portOfLoadingId: number;
  portOfDischargeId: number;
  voyageNumber?: string;
  vesselId?: number;
  carrierId?: number;
  /** FK a `tblVoyageCarriers.Id` (PK de la relación viaje-naviera). Persiste en `VoyageCarrierDetailId`. */
  voyageCarrierDetailId?: number;
  voyageCallLoadingId?: number;
  voyageCallDischargeId?: number;

  // Display-only (NO persisten en MSSQL — vienen del wizard init / apply-booking
  // y se usan solo para mostrar en la UI / PDF).
  vesselName?: string;
  carrierName?: string;
  portOfLoadingName?: string;
  portOfDischargeName?: string;
  customerName?: string;

  // ─── Operacional ─────────────────────────────────────────────────────────
  terminalPol?: string;
  /** ISO 8601 datetime con hora. Editable si no hay booking OPB. */
  etd?: string;
  /** ISO 8601 datetime con hora. */
  eta?: string;
  /** ISO 8601 datetime con HORA. Mapea a `tblShippingInstructions.DocumentalCutOffDate`. */
  cutOffDocumental?: string;
  /** ISO 8601 datetime con HORA. Mapea a `tblShippingInstructions.BeginStackingDate` (decisión v1). */
  cutOffFisico?: string;
  serviceType?: string;
  transitDays?: number;

  // ─── Booking ─────────────────────────────────────────────────────────────
  /** Null = booking externo / "sin booking". v1: 1 booking → 1 I/E activo. */
  bookingId?: number;
  bookingNumber?: string;

  // ─── Cliente / Exportador ────────────────────────────────────────────────
  customerId: number;
  customerReference?: string;

  // ─── Partes documentales ─────────────────────────────────────────────────
  shipperPartyId: number;
  shipperName: string;
  shipperAddress?: string;
  consigneePartyId: number;
  consigneeName: string;
  consigneeAddress?: string;
  /** OBLIGATORIO en v1 (feedback 2026-04-27). */
  notifyPartyId: number;
  notifyName: string;
  notifyAddress?: string;

  // ─── Datos documentales ──────────────────────────────────────────────────
  billOfLadingIssuer?: string;
  countryOfFinalDestination?: string;
  remarks?: string;

  // ─── Tipo de embarque (HEADER) ───────────────────────────────────────────
  typeOfShipmentId: TypeOfShipment;
  /** Solo si typeOfShipmentId = Pallet. NULL si Contenedor. */
  quantityOfPallets?: number;

  // ─── Antecedentes Financieros (HEADER, ampliados feedback 2026-04-27) ────
  /** FK a `tblCustomsClauseOfSales` (FOB, CIF, ...). */
  clauseOfSaleId?: number;
  /** FK a `tblCustomsTypeOfCargoes` (FRIGORIZADOS, ...). */
  typeOfCargoId?: number;
  /** FK a `tblCustomsMethodOfPayments` (COB1, ...). */
  methodOfPaymentId?: number;
  /** FK a `tblCustomsModeOfSales` (BAJO COND., ...). */
  modeOfSaleId?: number;
  /** FK `tblCustomsWayOfTransports` — Vía transporte (MARÍTIMA, ...). */
  wayOfTransportId?: number;
  /** Días IVV — `DaysOfPaymentIVV` en `tblShippingInstructions`. */
  daysOfPaymentIvv?: number;
  /** FK `tblCurrencies` — Moneda FOB — `ValorFOBCurrencyId`. */
  valorFobCurrencyId?: number;
  /** FK `tblParties` — Aduana de presentación — `CustomsId`. */
  customsId?: number;
  /**
   * Solo UI / PDF — nombre corto de la aduana elegida en el wizard (autocomplete).
   * No persiste como columna dedicada en MSSQL.
   */
  customsPresentationName?: string;
  /** Comentarios aduaneros — `CustomsRemarks`. */
  customsRemarks?: string;
  /**
   * Condición de Flete (Collect, Full Collect, Prepaid, Full Prepaid).
   * ⚠️ V1: NO persiste en MSSQL (no hay columna). Solo se renderiza en el PDF generado.
   * En v2 se podría agregar columna o lookup table.
   */
  freightCondition?: FreightCondition;
  /** Pallets Chep (sí/no). 🆕 feedback 2026-04-27. */
  palletsChep?: boolean;
  /** Cantidad de pallets CHEP. Persiste en `ChepPalletQuantity` (INT). */
  palletsChepQuantity?: number;
  /** SPS (sí/no). 🆕 feedback 2026-04-27. */
  sps?: boolean;
  /** Marca comercial (MDT, DOLE, ...). */
  brand?: string;
  /** Precio por caja (texto libre, ej: "10 USD"). */
  priceBox?: string;

  /**
   * Datos de contacto / RUT en UI (cache desde `tblPartyContacts` / `tblParties` al aplicar
   * booking o elegir parte en paso 2). No se persisten en columnas dedicadas del INSERT v1;
   * se usan en resumen paso 4 y PDF vía payload enriquecido si aplica.
   */
  shipperEmail?: string;
  shipperTaxIdentificationNumber?: string;
  shipperContactName?: string;
  shipperPhoneNumber?: string;
  shipperMobilePhoneNumber?: string;

  consigneeEmail?: string;
  consigneeTaxIdentificationNumber?: string;
  consigneeContactName?: string;
  consigneePhoneNumber?: string;
  consigneeMobilePhoneNumber?: string;

  notifyEmail?: string;
  notifyTaxIdentificationNumber?: string;
  notifyContactName?: string;
  notifyPhoneNumber?: string;
  notifyMobilePhoneNumber?: string;

  /** Cliente (portal): email / contacto / RUT para cabecera «Atención». */
  customerContactEmail?: string;
  customerAttentionContactName?: string;
  customerTaxIdentificationNumber?: string;

  /**
   * Solo UI: referencia interna devuelta por el POST al crear el I/E.
   * No debe reenviarse en el body del create.
   */
  createdInternalReference?: string;

  // ─── Items de carga ──────────────────────────────────────────────────────
  items: CreateShippingInstructionItemDto[];
}

/**
 * Ítem de carga. Mapea a `tblShippingInstructionItems`.
 *
 * NOTA v1: el N° de contenedor y sello NO se ingresan en este wizard
 * (van en módulo Couriers que es v2). Acá se ingresa solo cantidad +
 * tipo de bulto + parámetros del contenedor (temp, ventilación, etc.).
 */
export interface CreateShippingInstructionItemDto {
  itemNumber: number;

  /**
   * Cantidad de contenedores de esta línea (solo tipo embarque Contenedor).
   * Mapea a `QuantityOfContainers` en `tblShippingInstructionItems`.
   */
  quantityOfContainers?: number;

  /** Tipo de bulto del contenedor (REEFER40, DRY40, OPEN_TOP, etc.). FK a tipo de package. */
  containerType?: string;
  /**
   * FK `TypeOfPackagePrimaryTransportUnitContainerId` en `tblShippingInstructionItems`.
   * Preferido para persistencia; `containerType` puede mantenerse como código legible.
   */
  containerTypeId?: number;
  cargoType?: string;

  /**
   * Cantidad de pallets en la línea: obligatoria en instructivo tipo Pallet (ítem = línea de pallets).
   * En tipo Contenedor: cantidad de pallets asociada al ítem (opcional).
   */
  quantityOfPallets?: number;

  /**
   * FK `TypeOfPackagePrimaryTransportUnitPalletId` (`tblTypeOfPackagePrimaryTransportUnits`).
   * Preferido sobre texto; no usar IDs de `tblTypeOfPackages` (especies).
   */
  palletTypeId?: number;

  /** Código legible del tipo pallet (derivado del catálogo de transporte). */
  palletType?: string;

  // Parámetros del contenedor (NULL si Pallet)
  coldTreatment?: string;
  /**
   * FK `ColdTreatmentIndicatorId` — `tblParameterValues` ParameterId = 62
   * (ej. «Con tratamiento», «Sin tratamiento»). Preferido para persistencia.
   */
  coldTreatmentIndicatorId?: number | null;
  /**
   * Nombre legible de atmósfera controlada (ej. Afam, Extrafresh).
   * Mapea a `ControlledAtmosphereId` vía `tblControlledAtmospheres`.
   */
  atmosphere?: string;
  /**
   * FK `ControlledAtmosphereId` en `tblShippingInstructionItems`.
   * Preferido para persistencia; `atmosphere` se puede derivar del catálogo.
   */
  controlledAtmosphereId?: number | null;
  temperatureCelsius?: number;
  temperatureUnit?: 'C' | 'F';
  ventilation?: number;
  /**
   * FK `VentilationUnitId` — `tblParameterValues` ParameterId = 22
   * (NumericValue: 1 = %, 2 = CBM, …). Preferido para persistencia.
   */
  ventilationUnitId?: number | null;
  /** Código visible (%, CBM). Se rellena desde catálogo o booking. */
  ventilationUnit?: string;
  atmosphereParams?: string;
  humidity?: number;

  /** Observaciones del ítem. Mapea a `Remarks` en `tblShippingInstructionItems`. */
  itemRemarks?: string;

  /**
   * FK a `vwBookingContainers.Id` (BookingContainerId) del contenedor asignado.
   * Persiste en `tblShippingInstructionItems.BookingContainerId` para control de
   * disponibilidad en generación múltiple (v2). Null si booking externo / sin booking.
   */
  bookingContainerId?: number | null;

  /**
   * Especies del ítem (multi).
   * Mapea a `tblShippingInstructionItemPackages` en MSSQL
   * (Merchandise + Variety + boxes + weight + FOB).
   */
  species: CreateShippingInstructionSpeciesDto[];
}

export interface CreateShippingInstructionSpeciesDto {
  /** FK a `tblSpecies` u homólogo legacy (TBD). */
  speciesId?: number;
  speciesName: string;
  /** FK a catálogo de variedades (`tblMerchandiseVarieties.Id`). */
  varietyId?: number;
  variety?: string;
  /** FK a catálogo de condiciones (`tblMerchandiseConditions.Id`). */
  conditionId?: number;
  /** Texto del legacy: "Inspección SAG/Origen", "Sin tratamiento", etc. */
  condition?: string;
  boxCount: number;
  netWeightKgPerBox?: number;
  grossWeightKgPerBox?: number;
  /** Calculado: boxCount * netWeightKgPerBox. */
  totalNetWeightKg?: number;
  /** Calculado: boxCount * grossWeightKgPerBox. */
  totalGrossWeightKg?: number;
  /**
   * FK `TypeOfPackageId` en `tblShippingInstructionItemPackages`.
   * Preferido; `packageType` es el texto (NamePlural) derivado del catálogo.
   */
  typeOfPackageId?: number | null;
  /** Texto UI (BOXES, RACKS, …) — `tblTypeOfPackages.NamePlural`. */
  packageType?: string;
  /** Marca a nivel especie (puede diferir del header). */
  brand?: string;
  /** Número de orden de compra asociado a la especie. */
  purchaseOrderNumber?: string;
  /** Número de ítem de la orden de compra. */
  purchaseOrderItemNumber?: string;
  fobUnitPrice?: number;
  /** Calculado: boxCount * fobUnitPrice. */
  fobItemTotal?: number;
}

/** Opción de catálogo para combo de especies. */
export interface SpeciesCatalogItemDto {
  speciesId: number;
  speciesName: string;
}

/** Opción de catálogo para combo de variedades. */
export interface VarietyCatalogItemDto {
  varietyId: number;
  merchandiseId: number;
  varietyName: string;
}

/** Opción de catálogo para combo de condiciones de especie. */
export interface ConditionCatalogItemDto {
  conditionId: number;
  conditionName: string;
}

// ─── RESPONSE DTOs ──────────────────────────────────────────────────────────

export interface BookingSummaryDto {
  bookingId: number;
  bookingNumber: string;
  carrierBookingNumber?: string;
  customerId: number;
  customerName: string;
  /** Exportador (shipper) asociado al booking en OPB. */
  shipperName?: string;
  containerCount: number;
  packageCount: number;
  /** true si existe al menos un I/E activo para este booking. */
  hasActiveShippingInstruction: boolean;
  /** v2: true si hay contenedores disponibles (no asignados a I/Es activos). */
  hasAvailableContainers?: boolean;
  /** v2: cantidad de contenedores sin I/E activo asignado. */
  pendingContainerCount?: number;
}

export interface WizardInitResponseDto {
  voyageId: number;
  voyageNumber: string;
  vesselId: number;
  vesselName: string;
  carrierId: number;
  carrierName: string;
  portOfLoadingId: number;
  portOfLoadingName: string;
  portOfDischargeId: number;
  portOfDischargeName: string;
  voyageCallLoadingId?: number;
  voyageCallDischargeId?: number;

  terminalPol?: string;
  etd?: string;
  eta?: string;
  /** ISO datetime (con hora). */
  cutOffDocumental?: string;
  /** ISO datetime (con hora). */
  cutOffFisico?: string;
  serviceType?: string;
  transitDays?: number;

  /** FK `tblVoyageCarriers.Id` — PK de la relación viaje-naviera. */
  voyageCarrierDetailId?: number;

  isTerminalAvailable: boolean;
  isCutOffAvailable: boolean;

  /**
   * Emisor B/L (naviera emisora). Precarga v1: mismo criterio que booking (`CarrierName`).
   */
  billOfLadingIssuer?: string;
  /**
   * País destino final cuando se puede resolver vía dirección del consignatario del booking.
   */
  countryOfFinalDestination?: string;

  /** Texto descriptivo del horario de stacking (ej: "Desde Lunes 13 a las 08:00 hasta Viernes 17 a las 15:00"). */
  stackingDescription?: string;

  /** Bookings asociados al voyage. v1: filtrar los que ya tienen I/E activo si querés ocultarlos. */
  availableBookings: BookingSummaryDto[];
}

export interface PartyDetailDto {
  partyId: number;
  name: string;
  /** Razón social / nombre largo (`tblParties.FullName`). Opcional según SP. */
  fullName?: string;
  /** Nombre corto (`tblParties.ShortName`). Opcional; autocomplete consignatario lo usa en el combo. */
  shortName?: string;
  address?: string;
  /** Email del contacto principal. JOIN backend: `tblParties` → `tblPartyContacts.Email`. 🆕 */
  email?: string;
  /** RUT / identificación (`tblParties.NationalIdentificationNumber`). */
  taxIdentificationNumber?: string;
  /** Nombre legible del contacto principal (`tblPartyContacts` FirstName + LastName). */
  contactName?: string;
  /** Teléfono fijo del contacto principal. */
  phoneNumber?: string;
  /** Teléfono móvil del contacto principal. */
  mobilePhoneNumber?: string;
}

export interface ApplyBookingResponseDto {
  bookingId: number;
  bookingNumber: string;
  /** True si el bookingId existe en sistema OPB (BookingId NOT NULL). False = booking externo. 🆕 */
  isOpbBooking: boolean;
  /** True si el booking ya tiene al menos un I/E activo. */
  hasActiveShippingInstruction: boolean;
  /** v2: True si hay contenedores disponibles para crear un nuevo I/E. */
  hasAvailableContainers?: boolean;
  /** v2: Cantidad de contenedores sin I/E activo (propagado del BookingSummaryDto). */
  pendingContainerCount?: number;

  customerId: number;
  customerName: string;
  customerReference?: string;
  /**
   * Tipo de embarque real del booking OPB (1=Container, 2=Pallet, `TypeOfShipment` enum) —
   * antes el wizard forzaba "Contenedor" para todo booking OPB (feat-ie-wizard-mejoras, punto 1).
   * Solo lectura para bookings OPB: el dato viene del booking, no lo elige el usuario.
   */
  typeOfShipmentId?: TypeOfShipment;

  terminalPol?: string;
  etd?: string;
  cutOffDocumental?: string;
  cutOffFisico?: string;
  billOfLadingIssuer?: string;
  countryOfFinalDestination?: string;
  remarks?: string;

  /**
   * Email del cliente (`CustomerId` → `tblPartyContacts`), para cabecera del instructivo.
   */
  customerContactEmail?: string;
  /** Nombre del contacto principal del cliente (misma fila que email cuando aplica). */
  customerAttentionContactName?: string;
  /** RUT cliente (`tblParties.NationalIdentificationNumber`). */
  customerTaxIdentificationNumber?: string;
  /** Dirección del cliente (`tblPartyAddresses`). Se usa para pre-seleccionar al cliente como exportador. */
  customerAddress?: string;

  /**
   * Condición de flete desde booking OPB (`vwBookings.TermOfPaymentId` → enum, V6 2026-08-17 —
   * antes usaba `ShipConvenienceIndicatorName`, que solo toma valores "Sí"/"No" y nunca matcheaba
   * nada; ver `wizard.service.ts::mapFreightFromTermOfPayment()`).
   * Si el legado trae un `TermOfPaymentId` no mapeado, va solo `freightConditionBookingHint`.
   */
  freightCondition?: FreightCondition;
  /** Valor textual de la reserva cuando no se pudo mapear a `FreightCondition` (pista en el paso 3). */
  freightConditionBookingHint?: string;

  /** Si isOpbBooking=true: precarga shipper, consignee, notify. Si false: solo shipper (= cliente). */
  shipper?: PartyDetailDto;
  consignee?: PartyDetailDto;
  notify?: PartyDetailDto;

  items: BookingItemPreloadDto[];
  /** Lista de campos que el sistema precompletó (para mostrar en UI: "campos precargados automáticamente"). */
  preloadedFields: string[];
}

export interface BookingItemPreloadDto {
  /**
   * Id fila en `vwBookingContainers` — 1 por contenedor físico. El frontend debe llevarlo
   * intacto hasta `CreateShippingInstructionItemDto.bookingContainerId` (mismo nombre) para que
   * persista en `tblShippingInstructionItems.BookingContainerId` — es lo que usa
   * `sp_InstructivoEmbarque_ApplyBooking` para marcar el contenedor como ya no disponible.
   * Bug real (2026-08, booking 070ISA1237108): el form del wizard no tenía este control y los
   * contenedores quedaban "pendientes" para siempre. Ver `step3-carga-financieros.component.ts`.
   */
  bookingContainerId?: number;
  /** N° contenedor en la reserva OPB (referencia; el wizard v1 no lo persiste en ítem). */
  containerNumber?: string;
  containerType?: string;
  /** FK `tblTypeOfPackagePrimaryTransportUnits` cuando el JOIN en SP resuelve el tipo. */
  containerTypeId?: number;
  /** Líneas contenedor: típicamente 1 por fila de booking. */
  quantityOfContainers?: number;
  cargoType?: string;
  coldTreatmentIndicatorId?: number;
  controlledAtmosphereId?: number;
  /** Temperatura indicada en la reserva (sentido según `temperatureUnit`). */
  temperature?: number;
  temperatureUnit?: 'C' | 'F';
  ventilation?: number;
  ventilationUnitId?: number;
  /** Código legible (ej. %, CBM) desde `tblParameterValues` ParameterId 22. */
  ventilationUnit?: string;
  humidity?: number;
  /** Texto O2/CO2 u otros parámetros de atmósfera desde la reserva. */
  atmosphereParams?: string;
  packages: BookingPackagePreloadDto[];
}

export interface BookingPackagePreloadDto {
  /** Texto mercancía (legacy / booking); preferir `speciesName` si viene de OPB. */
  commodityDescription: string;
  /** FK `tblMerchandises.Id` cuando el SP lo resuelve. */
  speciesId?: number;
  speciesName?: string;
  hsCode?: string;
  packageType: string;
  packageQuantity: number;
  /** FK `tblTypeOfPackages.Id` cuando el JOIN por nombre coincide. */
  typeOfPackageId?: number;
  /** FK `tblMerchandiseVarieties.Id` si la vista de booking lo expone. */
  varietyId?: number;
  /** Nombre de la variedad (JOIN o vista). */
  varietyName?: string;
  /** Condición por defecto (primera activa en catálogo) para cumplir validadores del paso 3. */
  conditionId?: number;
  conditionName?: string;
  grossWeightKg?: number;
  volumeM3?: number;
}

export interface WizardValidationResultDto {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

/** Respuesta del POST /instructivo-embarque. */
export interface ShippingInstructionCreatedDto {
  id: number;
  /** Referencia interna humana (ej: "F-69"). Generada por backend con secuencia. */
  internalReference: string;
  statusId: number;
  statusName: string;
  createdAt: string;
  /** Path absoluto del PDF generado. */
  pdfPath?: string;
  /** Resultado del envío automático de email. */
  emailStatus: EmailStatus;
  emailSentAt?: string;
  /** Si emailStatus=FAILED, mensaje de error para mostrar en UI. */
  emailError?: string;
}

// ─── CATÁLOGOS ──────────────────────────────────────────────────────────────

export interface CatalogItemDto {
  id: number;
  name: string;
  /** Descripción adicional (ej: "Cobranza Documentaria" para "COB1"). */
  remarks?: string;
}

/** Tipos de bulto contenedor (`tblTypeOfPackagePrimaryTransportUnits`) para combo Paso 3. */
export interface ContainerPackageTypeDto {
  containerTypeId: number;
  /** Código visible (ej: CONT20, REEFER40). */
  code: string;
}

/**
 * Tipos de bulto pallet — unidad primaria (`tblTypeOfPackagePrimaryTransportUnits`).
 * Distinto de `SpeciesPackageTypeDto` (`tblTypeOfPackages`, embalaje por especie).
 */
export interface PalletPackageTypeDto {
  palletTypeId: number;
  code: string;
}

/** Indicadores de tratamiento frío (`tblParameterValues` ParameterId = 62). */
export interface ColdTreatmentIndicatorDto {
  coldTreatmentIndicatorId: number;
  /** Texto legacy (ej: Con tratamiento, Sin tratamiento). */
  name: string;
}

/** Atmósferas controladas (`tblControlledAtmospheres`) para combo Paso 3. */
export interface ControlledAtmosphereDto {
  controlledAtmosphereId: number;
  name: string;
}

/** Unidades de ventilación (`tblParameterValues` ParameterId = 22) — combo «Un. vent.». */
export interface VentilationUnitDto {
  ventilationUnitId: number;
  /** Código UI (%, CBM). */
  code: string;
}

/** Tipos de embalaje por especie (`tblTypeOfPackages`) — combo «Tipo embalaje». */
export interface SpeciesPackageTypeDto {
  typeOfPackageId: number;
  /** NamePlural (BOXES, RACKS, …). */
  code: string;
}

/** Catálogos para Antecedentes Financieros (Paso 3). */
export interface AntecedentesCatalogsDto {
  clauseOfSales: CatalogItemDto[]; // FOB, CIF, ...
  typeOfCargoes: CatalogItemDto[]; // FRIGORIZADOS, ...
  methodOfPayments: CatalogItemDto[]; // COB1, PPD, ...
  modeOfSales: CatalogItemDto[]; // BAJO COND., ...
  /** `tblCustomsWayOfTransports` — Vía transporte. */
  wayOfTransports: CatalogItemDto[];
  /** `tblCurrencies` — Moneda FOB (Name = Code o Id). */
  fobCurrencies: CatalogItemDto[];
  /** Si el legacy tiene tabla; si no, frontend usa enum FreightCondition hardcoded. */
  freightConditions: CatalogItemDto[];
}

// ─── LISTADO Y DETALLE (read-only) ──────────────────────────────────────────

export interface ShippingInstructionListItemDto {
  id: number;
  internalReference: string;
  voyageId: number;
  voyageNumber: string;
  vesselName: string;
  customerName: string;
  bookingNumber?: string;
  emissionDate: string;
  statusId: number;
  statusName: string;
  emailStatus: EmailStatus;
  /** Tipo de embarque resuelto a string para listado: 'Contenedor' | 'Pallet'. */
  typeOfShipmentName: string;
}

export interface ShippingInstructionListResponseDto {
  items: ShippingInstructionListItemDto[];
  total: number;
  page: number;
  size: number;
}

export interface ShippingInstructionDetailDto
  extends CreateShippingInstructionDto {
  id: number;
  internalReference: string;
  statusId: number;
  statusName: string;
  emailStatus: EmailStatus;
  emailSentAt?: string;
  emailError?: string;
  pdfPath?: string;
  createdAt: string;
  createdBy: number;
  createdByName?: string;
}

// ─── REQUEST DTOs V2 (edición y eliminación) ─────────────────────────────────

/** Campos editables de un I/E (v2). Solo se aplican si no son null/undefined. */
export interface UpdateShippingInstructionDto {
  consigneePartyId?: number;
  consigneeAddress?: string;
  notifyPartyId?: number;
  notifyAddress?: string;
  clauseOfSaleId?: number;
  typeOfCargoId?: number;
  methodOfPaymentId?: number;
  modeOfSaleId?: number;
  wayOfTransportId?: number;
  daysOfPaymentIvv?: number;
  valorFobCurrencyId?: number;
  remarks?: string;
}

/** Respuesta del PATCH /instructivo-embarque/:id. */
export interface ShippingInstructionUpdatedDto {
  id: number;
  internalReference: string;
  statusId: number;
  statusName: string;
  updatedAt: string;
  emailStatus?: EmailStatus;
}

/** Respuesta del DELETE /instructivo-embarque/:id. */
export interface ShippingInstructionSoftDeletedDto {
  id: number;
  internalReference: string;
  statusId: number;
  statusName: string;
  deletedAt: string;
  emailStatus?: EmailStatus;
}
